import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { DataGrid } from "@mui/x-data-grid";
import { deleteCategory, fetchCategoriesList } from "../../shared/catalogMastersApi";
import { IconEye, IconPlus } from "../../shared/toolbarIcons";
import { RecordViewDrawer } from "../../shared/RecordDrawer";
import { DEFAULT_PAGE_SIZE_OPTIONS } from "../../shared/useClientPagination";

function formatBool(value) {
  return value ? "Yes" : "No";
}

export function CategoriesList() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [viewRow, setViewRow] = useState(null);

  const categoryById = useMemo(() => {
    const m = new Map();
    for (const c of items) m.set(c.id, c);
    return m;
  }, [items]);

  const rows = useMemo(() => {
    return items.map((x) => {
      const parent = x.parentId != null ? categoryById.get(x.parentId) : null;
      return {
        id: x.id,
        category: x,
        parentName: parent?.name ?? "—"
      };
    });
  }, [items, categoryById]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const result = await fetchCategoriesList();
    if (!result.ok) {
      setError(result.error ?? "Failed to load data");
      setItems([]);
    } else {
      setItems(result.items);
    }
    setLoading(false);
  }, []);

  const handleDeleteClick = useCallback(
    async (id) => {
      if (!(await window.confirmDialog("Delete this category?"))) return;
      const res = await deleteCategory(id);
      if (!res.ok) setError(res.error ?? "Failed to delete category");
      else await load();
    },
    [load]
  );

  const columns = useMemo(
    () => [
      {
        field: "name",
        headerName: "Name",
        flex: 0.85,
        minWidth: 110,
        valueGetter: (_v, row) => row.category.name ?? "-"
      },
      {
        field: "parentName",
        headerName: "Parent",
        flex: 1,
        minWidth: 120,
        valueGetter: (_v, row) => row.parentName
      },
      {
        field: "slug",
        headerName: "Slug",
        flex: 0.9,
        minWidth: 100,
        valueGetter: (_v, row) => row.category.slug ?? "-"
      },
      {
        field: "order",
        headerName: "Order",
        width: 80,
        valueGetter: (_v, row) => row.category.order ?? 0
      },
      {
        field: "actions",
        headerName: "Actions",
        width: 160,
        align: "center",
        headerAlign: "center",
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: (params) => {
          const x = params.row.category;
          return (
            <div className="table-actions">
              <button className="icon-button" type="button" title="View" onClick={() => setViewRow(params.row)}>
                <IconEye />
              </button>
              <button className="icon-button" type="button" title="Edit" onClick={() => navigate(`/categories?edit=${x.id}`)}>
                ✏️
              </button>
              <button className="icon-button danger" type="button" title="Delete" onClick={() => void handleDeleteClick(x.id)}>
                🗑️
              </button>
            </div>
          );
        }
      }
    ],
    [navigate, handleDeleteClick]
  );

  useEffect(() => {
    void load();
  }, [load]);

  const total = rows.length;

  return (
    <div className="card">
      <div className="card-header card-header--row">
        <div>
          <h2>Categories</h2>
          <p>{loading ? "Loading categories..." : `Total: ${total}`}</p>
        </div>
        <div className="card-header-actions">
          <Link to="/categories" className="icon-button icon-button--toolbar" title="Add category" aria-label="Add category">
            <IconPlus />
          </Link>
        </div>
      </div>
      <div className="card-body">
        {error && <div className="error-banner">{error}</div>}
        <div className="masters-data-grid-shell">
          <DataGrid
            rows={rows}
            columns={columns}
            loading={loading}
            disableRowSelectionOnClick
            pagination
            paginationMode="client"
            pageSizeOptions={DEFAULT_PAGE_SIZE_OPTIONS}
            initialState={{
              pagination: { paginationModel: { page: 0, pageSize: DEFAULT_PAGE_SIZE_OPTIONS[0] } }
            }}
            sx={{
              border: "none",
              fontFamily: "inherit",
              fontSize: "0.9rem",
              height: "100%",
              "& .MuiDataGrid-columnHeaders": {
                backgroundColor: "#f2fcf5",
                borderBottom: "1px solid #e8ecea",
                fontWeight: 600
              },
              "& .MuiDataGrid-footerContainer": {
                borderTop: "1px solid #e8ecea",
                backgroundColor: "#fbfffc"
              },
              "& .MuiDataGrid-row:nth-of-type(even):not(.MuiDataGrid-row--editing):not(:hover)": {
                backgroundColor: "#fcfffd"
              },
              "& .MuiDataGrid-cell": {
                borderBottomColor: "#e8ecea"
              }
            }}
          />
        </div>
      </div>
      <RecordViewDrawer
        open={Boolean(viewRow)}
        onClose={() => setViewRow(null)}
        label="Category details"
        title={viewRow?.category?.name ?? "—"}
        subtitle={viewRow?.category?.slug}
        ariaLabel="Category details"
        images={
          viewRow
            ? [
                viewRow.category.image ? { label: "Image", src: viewRow.category.image, linkText: "Open image" } : null,
                viewRow.category.icon ? { label: "Icon", src: viewRow.category.icon, linkText: "Open icon" } : null
              ].filter(Boolean)
            : []
        }
        sections={
          viewRow
            ? [
                {
                  title: "Category Details",
                  columns: 2,
                  fields: [
                    ["Name", viewRow.category.name ?? "-"],
                    ["Parent", viewRow.parentName ?? "—"],
                    ["Slug", viewRow.category.slug ?? "-"],
                    ["Order", viewRow.category.order ?? 0],
                    ["Active", formatBool(viewRow.category.isActive)],
                    ["Featured", formatBool(viewRow.category.isFeatured)]
                  ]
                },
                {
                  title: "SEO Details",
                  fields: [
                    ["Meta Title", viewRow.category.metaTitile ?? "-"],
                    ["Meta Description", viewRow.category.metaDescription ?? "-", true]
                  ]
                }
              ]
            : []
        }
      />
    </div>
  );
}
